import React, { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import Container from '@embracesbs/component-container'
import Button2 from '@embracesbs/component-button2'
import Text from '@embracesbs/component-text'
import TextField from '@embracesbs/component-textfield'
import Autocomplete from '@embracesbs/component-autocomplete'
import Table from '@embracesbs/component-table'
import TableRow from '@embracesbs/component-tablerow'
import EmptyState from '@embracesbs/component-emptystate'
import { downloadFile } from '../../Utilities/helpers/helpers'

interface TrivyVulnerability {
  VulnerabilityID?: string
  PkgName?: string
  InstalledVersion?: string
  FixedVersion?: string
  Severity?: string
  Title?: string
  PrimaryURL?: string
}

interface TrivyResult {
  Target?: string
  Class?: string
  Type?: string
  Vulnerabilities?: TrivyVulnerability[]
}

interface TrivyReportDocument {
  ArtifactName?: string
  Results?: TrivyResult[]
  // trivy k8s scans nest per-resource reports instead of a flat Results array
  Resources?: { Name?: string; Namespace?: string; Results?: TrivyResult[] }[]
}

interface TrivyFinding {
  artifact: string
  namespace: string
  target: string
  vulnerabilityId: string
  pkgName: string
  installedVersion: string
  fixedVersion: string
  severity: string
  title: string
  primaryUrl: string
}

const SEVERITY_ORDER: Record<string, number> = {
  CRITICAL: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
  UNKNOWN: 4
}

const severityColor = (severity: string): string => {
  switch (severity.toUpperCase()) {
    case 'CRITICAL':
      return 'var(--tma-status-failed)'
    case 'HIGH':
      return 'var(--tma-status-failed)'
    case 'MEDIUM':
      return 'var(--tma-status-wait)'
    case 'LOW':
      return 'var(--tma-status-ok)'
    default:
      return 'var(--tma-status-unknown)'
  }
}

const csvCell = (value: string): string => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value)

const findingsToCsv = (findings: TrivyFinding[]): string => {
  const headers = ['Severity', 'Vulnerability ID', 'Package', 'Installed version', 'Fixed version', 'Namespace', 'Target', 'Title', 'URL']
  const rows = findings.map((finding) => [
    finding.severity,
    finding.vulnerabilityId,
    finding.pkgName,
    finding.installedVersion,
    finding.fixedVersion,
    finding.namespace,
    finding.target,
    finding.title,
    finding.primaryUrl
  ])
  return [headers, ...rows].map((row) => row.map(csvCell).join(',')).join('\n')
}

const flattenResults = (results: TrivyResult[] | undefined, artifact: string, namespace: string): TrivyFinding[] => {
  if (!results) return []
  return results.flatMap((result) =>
    (result.Vulnerabilities ?? []).map((vuln) => ({
      artifact,
      namespace,
      target: result.Target ?? '–',
      vulnerabilityId: vuln.VulnerabilityID ?? '–',
      pkgName: vuln.PkgName ?? '–',
      installedVersion: vuln.InstalledVersion ?? '–',
      fixedVersion: vuln.FixedVersion ?? '–',
      severity: (vuln.Severity ?? 'UNKNOWN').toUpperCase(),
      title: vuln.Title ?? '',
      primaryUrl: vuln.PrimaryURL ?? ''
    }))
  )
}

// Trivy's JSON shape varies by scan target: a single "trivy image" run is one document with a
// flat Results array, a "trivy k8s" run nests Results per Resource, and some CI setups concatenate
// multiple image scans into a top-level array. Normalize all three into one findings list.
const parseFindings = (content: unknown): TrivyFinding[] | null => {
  if (!content || typeof content !== 'object') return null

  const documents: TrivyReportDocument[] = Array.isArray(content) ? content : [content as TrivyReportDocument]

  return documents.flatMap((doc) => {
    const artifact = doc.ArtifactName ?? '–'
    if (doc.Resources) {
      return doc.Resources.flatMap((resource) =>
        flattenResults(resource.Results, resource.Namespace ? `${resource.Namespace}/${resource.Name}` : (resource.Name ?? artifact), resource.Namespace ?? '')
      )
    }
    return flattenResults(doc.Results, artifact, '')
  })
}

interface TrivyColumn {
  key: string
  header: string
  tooltip?: string
  style: Record<string, unknown>
  render: (finding: TrivyFinding) => React.ReactNode
  sortValue?: (finding: TrivyFinding) => number | string
}

const COLUMNS: TrivyColumn[] = [
  {
    key: 'severity',
    header: 'Severity',
    style: { width: 110, leftSpacing: 16 },
    sortValue: (finding) => SEVERITY_ORDER[finding.severity] ?? 99,
    render: (finding) => <Text style={{ color: severityColor(finding.severity), fontWeight: 600 }}>{finding.severity}</Text>
  },
  {
    key: 'vulnerabilityId',
    header: 'Vulnerability',
    style: { width: 150, leftSpacing: 16 },
    sortValue: (finding) => finding.vulnerabilityId,
    render: (finding) =>
      finding.primaryUrl ? (
        <a href={finding.primaryUrl} target='_blank' rel='noreferrer'>
          {finding.vulnerabilityId}
        </a>
      ) : (
        <Text>{finding.vulnerabilityId}</Text>
      )
  },
  {
    key: 'pkgName',
    header: 'Package',
    style: { width: 220, leftSpacing: 16 },
    sortValue: (finding) => finding.pkgName,
    render: (finding) => <Text>{finding.pkgName}</Text>
  },
  {
    key: 'versions',
    header: 'Installed → Fixed',
    style: { width: 200, leftSpacing: 16 },
    sortValue: (finding) => finding.installedVersion,
    render: (finding) => (
      <Text>
        {finding.installedVersion}
        {finding.fixedVersion !== '–' ? ` → ${finding.fixedVersion}` : ''}
      </Text>
    )
  },
  {
    key: 'target',
    header: 'Target',
    style: { width: 260, leftSpacing: 16 },
    sortValue: (finding) => finding.target,
    render: (finding) => (
      <Text>
        <span data-tooltip-id='global-tooltip' data-tooltip-content={`${finding.artifact} — ${finding.target}`}>
          {finding.target}
        </span>
      </Text>
    )
  },
  {
    key: 'title',
    header: 'Title',
    style: { width: 360, leftSpacing: 16 },
    sortValue: (finding) => finding.title,
    render: (finding) => (
      <Text>
        <span data-tooltip-id='global-tooltip' data-tooltip-content={finding.title}>
          {finding.title || '–'}
        </span>
      </Text>
    )
  }
]

const TABLE_MIN_WIDTH = COLUMNS.reduce((sum, column) => sum + (Number(column.style.width) || 0), 0)

type SortDirection = 'asc' | 'desc'
type SortState = { key: string; direction: SortDirection } | null

const PAGE_SIZE = 200

interface FilterOption {
  label: string
  value: string
}

const ColumnHeaderRow: React.FC<{ sort: SortState; onSort: (key: string) => void }> = ({ sort, onSort }) => (
  <TableRow>
    {COLUMNS.map((column) => {
      const isSorted = sort?.key === column.key
      const arrow = isSorted ? (sort?.direction === 'asc' ? ' ↑' : ' ↓') : ''
      return (
        <Text
          key={column.key}
          textStyle='description'
          style={{ color: isSorted ? 'var(--tma-text-strong)' : 'var(--tma-text-muted)', fontWeight: 600, fontSize: '10px', letterSpacing: '0.02em', textTransform: 'uppercase' }}
        >
          <span
            data-tooltip-id='global-tooltip'
            data-tooltip-content={column.tooltip ?? column.header}
            onClick={column.sortValue ? () => onSort(column.key) : undefined}
            style={column.sortValue ? { cursor: 'pointer' } : undefined}
          >
            {column.header}
            {arrow}
          </span>
        </Text>
      )
    })}
  </TableRow>
)

const FindingsTable: React.FC<{ findings: TrivyFinding[]; sort: SortState; onSort: (key: string) => void; rowKeyPrefix: string; pageOffset: number }> = ({
  findings,
  sort,
  onSort,
  rowKeyPrefix,
  pageOffset
}) => (
  <div style={{ overflowX: 'auto' }}>
    <div style={{ minWidth: `${TABLE_MIN_WIDTH}px` }}>
      <Table isTableFixed columnStyle={COLUMNS.map((column) => column.style)}>
        <ColumnHeaderRow sort={sort} onSort={onSort} />
        {findings.map((finding, index) => (
          <TableRow key={`${rowKeyPrefix}-${finding.target}-${finding.vulnerabilityId}-${finding.pkgName}-${pageOffset + index}`}>
            {COLUMNS.map((column) => (
              <React.Fragment key={column.key}>{column.render(finding)}</React.Fragment>
            ))}
          </TableRow>
        ))}
      </Table>
    </div>
  </div>
)

const PaginationControls: React.FC<{ currentPage: number; pageCount: number; total: number; onPrev: () => void; onNext: () => void }> = ({
  currentPage,
  pageCount,
  total,
  onPrev,
  onNext
}) => (
  <Container justify='space-between' isFullWidth style={{ gap: '12px', flexWrap: 'wrap' }}>
    <Text textStyle='description' textColor='grey'>
      Showing {currentPage * PAGE_SIZE + 1}–{Math.min((currentPage + 1) * PAGE_SIZE, total)} of {total}
    </Text>
    <Container style={{ gap: '8px', alignItems: 'center' }}>
      <Button2 text='← Prev' onClick={onPrev} isDisabled={currentPage === 0} variant='outlined' type='button' />
      <Text textStyle='description' textColor='grey' style={{ minWidth: '90px', textAlign: 'center' }}>
        Page {currentPage + 1} of {pageCount}
      </Text>
      <Button2 text='Next →' onClick={onNext} isDisabled={currentPage >= pageCount - 1} variant='outlined' type='button' />
    </Container>
  </Container>
)

interface NamespaceGroupProps {
  namespace: string
  findings: TrivyFinding[]
  isExpanded: boolean
  onToggle: () => void
  sort: SortState
  onSort: (key: string) => void
  page: number
  onPageChange: (page: number) => void
}

const NamespaceGroup: React.FC<NamespaceGroupProps> = ({ namespace, findings, isExpanded, onToggle, sort, onSort, page, onPageChange }) => {
  const bySeverity: Record<string, number> = {}
  findings.forEach((finding) => {
    bySeverity[finding.severity] = (bySeverity[finding.severity] ?? 0) + 1
  })
  const breakdown = Object.keys(bySeverity).sort((a, b) => (SEVERITY_ORDER[a] ?? 99) - (SEVERITY_ORDER[b] ?? 99))

  const pageCount = Math.max(1, Math.ceil(findings.length / PAGE_SIZE))
  const currentPage = Math.min(page, pageCount - 1)
  const pagedFindings = findings.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE)

  return (
    <div style={{ border: '1px solid var(--tma-list-card-border)', borderRadius: '6px', overflow: 'hidden' }}>
      <button
        type='button'
        onClick={onToggle}
        style={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: '10px',
          cursor: 'pointer',
          border: 'none',
          background: 'var(--tma-panel-bg)',
          padding: '10px 14px',
          textAlign: 'left'
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', minWidth: 0 }}>
          <span style={{ fontSize: '11px', color: 'var(--tma-text-muted)', flexShrink: 0 }}>{isExpanded ? '▾' : '▸'}</span>
          <Text style={{ fontWeight: 600, fontSize: '13px', textAlign: 'left', whiteSpace: 'nowrap' }}>{namespace}</Text>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            {breakdown.map((severity) => (
              <span key={severity} style={{ fontSize: '10px', fontWeight: 600, color: severityColor(severity), whiteSpace: 'nowrap' }}>
                {bySeverity[severity]} {severity}
              </span>
            ))}
          </div>
        </div>
        <Text style={{ fontSize: '12px', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0 }}>{findings.length}</Text>
      </button>

      {isExpanded && (
        <div style={{ borderTop: '1px solid var(--tma-list-card-border)', padding: '10px 14px 14px' }}>
          <FindingsTable findings={pagedFindings} sort={sort} onSort={onSort} rowKeyPrefix={namespace} pageOffset={currentPage * PAGE_SIZE} />
          {pageCount > 1 && (
            <div style={{ marginTop: '10px' }}>
              <PaginationControls
                currentPage={currentPage}
                pageCount={pageCount}
                total={findings.length}
                onPrev={() => onPageChange(Math.max(0, currentPage - 1))}
                onNext={() => onPageChange(Math.min(pageCount - 1, currentPage + 1))}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

const TrivyReportView: React.FC<{ content: unknown }> = ({ content }) => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [query, setQuery] = useState('')
  const [selectedSeverities, setSelectedSeverities] = useState<FilterOption[]>([])
  // Seeded once from a shared ?namespace=a,b link so opening that URL lands pre-filtered;
  // kept in sync back to the URL below so the current filter can be shared in turn.
  const [selectedNamespaces, setSelectedNamespaces] = useState<FilterOption[]>(() => {
    const raw = searchParams.get('namespace')
    if (!raw) return []
    return raw
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean)
      .map((value) => ({ label: value, value }))
  })
  const [sort, setSort] = useState<SortState>(null)
  const [page, setPage] = useState(0)
  const [expandedNamespaces, setExpandedNamespaces] = useState<Set<string>>(() => {
    const raw = searchParams.get('namespace')
    if (!raw) return new Set()
    return new Set(
      raw
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean)
    )
  })
  const [pageByNamespace, setPageByNamespace] = useState<Record<string, number>>({})

  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (selectedNamespaces.length > 0) next.set('namespace', selectedNamespaces.map((option) => option.value).join(','))
        else next.delete('namespace')
        return next
      },
      { replace: true }
    )
  }, [selectedNamespaces, setSearchParams])

  // Picking a namespace from the filter (not just landing on a link) should open it too,
  // rather than leaving the user to also click the now-lone accordion row.
  useEffect(() => {
    if (selectedNamespaces.length === 0) return
    setExpandedNamespaces((prev) => {
      const next = new Set(prev)
      selectedNamespaces.forEach((option) => next.add(option.value))
      return next
    })
  }, [selectedNamespaces])

  const toggleSort = (key: string) => {
    setSort((prev) => {
      if (!prev || prev.key !== key) return { key, direction: 'asc' }
      if (prev.direction === 'asc') return { key, direction: 'desc' }
      return null
    })
  }

  const toggleNamespaceExpanded = (namespace: string) => {
    setExpandedNamespaces((prev) => {
      const next = new Set(prev)
      if (next.has(namespace)) next.delete(namespace)
      else next.add(namespace)
      return next
    })
  }

  const findings = useMemo(() => parseFindings(content), [content])

  const severityOptions = useMemo<FilterOption[]>(() => {
    const found = new Set<string>()
      ; (findings ?? []).forEach((finding) => found.add(finding.severity))
    return Array.from(found)
      .sort((a, b) => (SEVERITY_ORDER[a] ?? 99) - (SEVERITY_ORDER[b] ?? 99))
      .map((severity) => ({ label: severity, value: severity }))
  }, [findings])

  // Only k8s scans carry a namespace per finding — a plain image scan has none, so the filter
  // and the grouped view below simply don't render rather than showing an empty, useless control.
  const namespaceOptions = useMemo<FilterOption[]>(() => {
    const found = new Set<string>()
      ; (findings ?? []).forEach((finding) => {
        if (finding.namespace) found.add(finding.namespace)
      })
    return Array.from(found)
      .sort()
      .map((namespace) => ({ label: namespace, value: namespace }))
  }, [findings])

  const isGrouped = namespaceOptions.length > 0

  useEffect(() => {
    setPage(0)
    setPageByNamespace({})
  }, [query, selectedSeverities, selectedNamespaces, sort])

  if (findings === null) {
    return (
      <Text textColor='grey' align='center'>
        This report could not be parsed as Trivy JSON.
      </Text>
    )
  }

  const activeSeverities = selectedSeverities.length ? new Set(selectedSeverities.map((option) => option.value)) : null
  const activeNamespaces = selectedNamespaces.length ? new Set(selectedNamespaces.map((option) => option.value)) : null

  const normalizedQuery = query.trim().toLowerCase()
  const matchesFilters = (finding: TrivyFinding) => {
    if (activeSeverities !== null && !activeSeverities.has(finding.severity)) return false
    if (activeNamespaces !== null && !activeNamespaces.has(finding.namespace)) return false
    if (!normalizedQuery) return true
    const haystack = `${finding.vulnerabilityId} ${finding.pkgName} ${finding.target} ${finding.artifact} ${finding.title}`.toLowerCase()
    return haystack.includes(normalizedQuery)
  }

  // Already globally sorted, so grouping preserves per-group order without re-sorting.
  const visibleFindings = findings.filter(matchesFilters).sort((a, b) => {
    if (sort) {
      const column = COLUMNS.find((candidate) => candidate.key === sort.key)
      if (column?.sortValue) {
        const valueA = column.sortValue(a)
        const valueB = column.sortValue(b)
        const compared = valueA < valueB ? -1 : valueA > valueB ? 1 : 0
        return sort.direction === 'asc' ? compared : -compared
      }
    }
    const rankA = SEVERITY_ORDER[a.severity] ?? 99
    const rankB = SEVERITY_ORDER[b.severity] ?? 99
    return rankA - rankB
  })

  const groupedFindings = isGrouped
    ? Array.from(
      visibleFindings.reduce((groups, finding) => {
        const key = finding.namespace || '(no namespace)'
        const list = groups.get(key) ?? []
        list.push(finding)
        groups.set(key, list)
        return groups
      }, new Map<string, TrivyFinding[]>())
    )
      .map(([namespace, list]) => ({ namespace, findings: list }))
      .sort((a, b) => b.findings.length - a.findings.length)
    : []

  // A report can carry tens of thousands of findings — page the table instead of rendering
  // every row as a DOM node, which is what actually makes a large report unusable, not the fetch.
  const pageCount = Math.max(1, Math.ceil(visibleFindings.length / PAGE_SIZE))
  const currentPage = Math.min(page, pageCount - 1)
  const pagedFindings = visibleFindings.slice(currentPage * PAGE_SIZE, currentPage * PAGE_SIZE + PAGE_SIZE)

  const exportSelection = () => {
    const suffix = selectedNamespaces.length === 1 ? `-${selectedNamespaces[0].value}` : ''
    downloadFile(`trivy-findings${suffix}.csv`, findingsToCsv(visibleFindings), 'text/csv;charset=utf-8')
  }

  return (
    <div>
      <Container justify='space-between' isFullWidth style={{ gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <Container style={{ gap: '18px', alignItems: 'center' }}>
          <Text textStyle='description'>Vulnerabilities: {findings.length}</Text>
          <Button2
            text={`Export selection (${visibleFindings.length})`}
            onClick={exportSelection}
            isDisabled={visibleFindings.length === 0}
            variant='outlined'
            type='button'
          />
        </Container>
        <div className='search-no-glow' style={{ minWidth: '240px' }}>
          <TextField
            isSearchMode
            placeholder='Search package, CVE, target…'
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClear={() => setQuery('')}
            type='text'
            variant='default'
          />
        </div>
      </Container>

      <Container style={{ gap: '10px', marginBottom: '12px', flexWrap: 'wrap' }}>
        <div style={{ minWidth: '220px' }}>
          <Autocomplete
            isMulti
            isClearable
            isSearchable
            placeholder='All severities'
            options={severityOptions}
            value={selectedSeverities}
            onChange={(selected: FilterOption[] | null) => setSelectedSeverities(selected ?? [])}
          />
        </div>
        {namespaceOptions.length > 0 && (
          <div style={{ minWidth: '260px' }}>
            <Autocomplete
              isMulti
              isClearable
              isSearchable
              placeholder='All namespaces'
              options={namespaceOptions}
              value={selectedNamespaces}
              onChange={(selected: FilterOption[] | null) => setSelectedNamespaces(selected ?? [])}
            />
          </div>
        )}
      </Container>

      {isGrouped ? (
        groupedFindings.length === 0 ? (
          <EmptyState imageName='emptyfolder' size='medium' title='No vulnerabilities match' text='Try a different search, severity, or namespace filter.' />
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {groupedFindings.map((group) => (
              <NamespaceGroup
                key={group.namespace}
                namespace={group.namespace}
                findings={group.findings}
                isExpanded={expandedNamespaces.has(group.namespace)}
                onToggle={() => toggleNamespaceExpanded(group.namespace)}
                sort={sort}
                onSort={toggleSort}
                page={pageByNamespace[group.namespace] ?? 0}
                onPageChange={(nextPage) => setPageByNamespace((prev) => ({ ...prev, [group.namespace]: nextPage }))}
              />
            ))}
          </div>
        )
      ) : (
        <>
          {visibleFindings.length === 0 ? (
            <EmptyState imageName='emptyfolder' size='medium' title='No vulnerabilities match' text='Try a different search or severity filter.' />
          ) : (
            <FindingsTable findings={pagedFindings} sort={sort} onSort={toggleSort} rowKeyPrefix='flat' pageOffset={currentPage * PAGE_SIZE} />
          )}

          {pageCount > 1 && (
            <div style={{ marginTop: '12px' }}>
              <PaginationControls
                currentPage={currentPage}
                pageCount={pageCount}
                total={visibleFindings.length}
                onPrev={() => setPage((prev) => Math.max(0, prev - 1))}
                onNext={() => setPage((prev) => Math.min(pageCount - 1, prev + 1))}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default TrivyReportView
